import { useContext } from 'react';
import { WizardContext } from './ProductionScheduleWizardContext';
import type { BatchDeterminationOverride, WizardBatchData } from './ProductionScheduleWizardContext';

export const useWizardContext = () => {
  const context = useContext(WizardContext);
  
  if (!context) {
    // Development-only fallback for hot reload scenarios
    if (process.env.NODE_ENV === 'development') {
      console.warn('useWizardContext: Using fallback context - this usually happens during hot reload. If this persists, please refresh the page.');
      
      // Return a minimal fallback context that won't interfere with real data
      // Helper function to create a full DateTime string (with time component) from a date
      const createDateTimeString = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}T00:00:00`;
      };

      return {
        // Form data
        formData: {
          plannedStart: createDateTimeString(new Date()),
          plannedEnd: createDateTimeString(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
          name: 'Production Schedule'
        },
        planningSettings: {
          defaultDays: 30,
          minDays: 1,
          maxDays: 365,
          allowOverride: true
        },
        isLoadingSettings: false,
        
        // Demands and materials - empty arrays to avoid confusion
        demands: [],
        orders: [],
        forecasts: [],
        effectiveDemands: [],
        productionEffectiveDemands: [],
        productionDisplayDemands: [],
        productionDisplayDemandsAggregated: [],
        pendingOrders: [],
        pendingForecasts: [],
        pendingDeletions: [],
        materials: [],
        isLoadingDemands: false,
        
        // Batch calculation
        selectedMaterialIndex: 0,
        batchMethod: 'even',
        batches: [],
        localBatchesMap: {},
        
        // Production Orders
        productionOrders: [],
        isCreatingProductionOrders: false,
        productionOrderCreationProgress: 0,
        
        // Validation state
        step3ValidationError: null,
        routingValidationIssues: null,
        
        // UI state
        currentStep: 0,
        planningMode: 'NEW_PLANNING' as const,
        sourceScheduleUuid: null,
        sourceSchedulePlannedStart: null,
        frozenZoneEnd: null,
        fixedDoughBatchesByMaterial: {},
        step0ReleasedPlansLoading: false,
        step0HasReleasedPlans: false,
        step1ReplanningHasNewSollBelowIst: false,
        lockedReplanningMaterialIds: new Set<string>(),
        settingsLoadedRef: { current: false }, // Mark as not loaded

        // Step 0 actions
        setPlanningMode: () => {},
        setSourceScheduleUuid: () => {},
        setSourceSchedulePlannedStart: () => {},
        setFrozenZoneEnd: () => {},
        setStep0ReleasedPlansState: () => {},
        setStep1ReplanningHasNewSollBelowIst: () => {},
        setLockedReplanningMaterialIds: () => {},
        
        // Actions - provide no-op functions that don't cause errors
        updateFormData: () => {
          console.warn('useWizardContext: updateFormData called on fallback context - data will not be saved');
        },
        setCurrentStep: () => {
          console.warn('useWizardContext: setCurrentStep called on fallback context - navigation disabled');
        },
        handleNext: () => {
          console.warn('useWizardContext: handleNext called on fallback context - navigation disabled');
        },
        handlePrevious: () => {
          console.warn('useWizardContext: handlePrevious called on fallback context - navigation disabled');
        },
        setSelectedMaterialIndex: () => {},
        setBatchMethod: (_method: 'even' | 'full' | 'individual') => {},
        setBatches: () => {},
        updateBatch: () => {},
        updateBatchesForMaterial: () => {},
        setLocalBatchesMap: () => {},
        loadDemands: async () => {
          console.warn('useWizardContext: loadDemands called on fallback context - no data will be loaded');
        },
        loadMaterials: async () => {
          console.warn('useWizardContext: loadMaterials called on fallback context - no data will be loaded');
        },
        addOrUpdatePendingOrder: () => {},
        addOrUpdatePendingForecast: () => {},
        clearPendingDemandCell: () => {},
        addPendingOrdersFromImport: () => {},
        addPendingForecastsFromImport: () => {},
        addPendingDeletion: () => {},
        savePendingStep1Demands: async () => {},
        registerStep1FlushPendingEdits: () => {},
        // Production Order actions
        createProductionOrders: async () => ({ success: false, error: 'Fallback context' }),
        deleteProductionOrders: async () => {},
        validateStep3: () => ({ isValid: false, batchesWithoutRouting: [] }),
        clearStep3ValidationError: () => {},
        
        generateDefaultScheduleName: () => 'Production Schedule (Fallback)',
        convertDemandsForView: () => [],
        formatDate: (date: string) => date,
        calculateBatches: () => [],
        setBatchDeterminationData: () => {},
        setBatchDeterminationOverride: () => {},
        calculateBatchDetermination: () => {},
        adjustBatchCountForSequence: () => {},
        removeBatchDeterminationData: () => {},
        setSelectedDayOfWeek: () => {},
        setSelectedSequenceUuid: () => {},
        setAvailableSequencesForDay: () => {},
        setSequenceEntries: () => {},
        addSequenceEntry: () => {},
        updateSequenceEntry: () => {},
        deleteSequenceEntry: () => {},
        setAutoAddedProductIds: () => {},
        validateSequenceEntries: () => ({ isValid: true, errors: [] }),
        batchDeterminationData: {} as Record<string, WizardBatchData>,
        batchDeterminationOverrides: {} as Record<string, BatchDeterminationOverride>,
        selectedDayOfWeek: null,
        selectedSequenceUuid: null,
        availableSequencesForDay: [],
        sequenceEntries: [],
        autoAddedProductIds: new Set<string>(),
        registerDirectStartSave: () => {},
        startPlanningWithDefaults: async () => false,
      } as any; // Type assertion to avoid type errors in fallback context
    }
    
    // Production: Always throw error
    throw new Error('useWizardContext must be used within a WizardProvider');
  }
  return context;
};
